import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ApiService } from '../../Shared/Services/api.service';
import { endpoints } from '../../../../environments/endpoints';
import { COUNT_PROBE_PAGE_SIZE } from '../../../Consts/backoffice.const';
import { LaravelDefaultResponse } from '../../../Interfaces/api/shared/response.interface';
import {
  OrganizationBillingEmailResponse,
  OrganizationListResponse,
  OrganizationRootChangeResponse,
  OrganizationShowResponse,
} from '../../../Interfaces/api/backoffice/organization.interface';
import { OrganizationEditPayload } from '../Interfaces/organization-edit.interface';
import { TypedResourceRequest } from '../../Shared/Interfaces/api-request.interface';
import { OrganizationFilters } from '../Interfaces/organization-filters.interface';
import { OrganizationDetail } from '../../../Models/backoffice/organization-detail.model';
import { OrganizationSummary } from '../../../Models/backoffice/organization-summary.model';
import { PagedResult } from '../../Shared/Interfaces/paged-result.interface';

@Injectable({
  providedIn: 'root',
})
export class OrganizationService {
  private readonly _apiService = inject(ApiService);

  listRequest(
    filters: Partial<OrganizationFilters>,
    page: number,
    pageSize: number,
  ): TypedResourceRequest<LaravelDefaultResponse<OrganizationListResponse>> {
    return this._apiService.get<LaravelDefaultResponse<OrganizationListResponse>>(
      endpoints.backoffice.organizations,
      { params: this._params(filters, page, pageSize) },
    );
  }

  countRequest(
    status?: string,
  ): TypedResourceRequest<LaravelDefaultResponse<OrganizationListResponse>> {
    return this._apiService.get<LaravelDefaultResponse<OrganizationListResponse>>(
      endpoints.backoffice.organizations,
      {
        params: this._params(status ? { status } : {}, 0, COUNT_PROBE_PAGE_SIZE),
        skipLoading: true,
      },
    );
  }

  showRequest(
    organizationId: string,
  ): TypedResourceRequest<LaravelDefaultResponse<OrganizationShowResponse>> {
    return this._apiService.get<LaravelDefaultResponse<OrganizationShowResponse>>(
      endpoints.backoffice.organization(organizationId),
      { skipLoading: true },
    );
  }

  toPagedResult(
    response: LaravelDefaultResponse<OrganizationListResponse> | undefined,
  ): PagedResult<OrganizationSummary> {
    const payload = response?.data;

    return {
      items: (payload?.organizations ?? []).map((row) => new OrganizationSummary(row)),
      total: payload?.total ?? 0,
      page: payload?.page ?? 0,
      pageSize: payload?.pageSize ?? 0,
    };
  }

  toDetail(
    response: LaravelDefaultResponse<OrganizationShowResponse> | undefined,
  ): OrganizationDetail | null {
    return response?.data?.organization ? new OrganizationDetail(response.data.organization) : null;
  }

  exportCsv(filters: Partial<OrganizationFilters>): Observable<Blob> {
    return this._apiService.download(
      this._apiService.get<Blob>(endpoints.backoffice.organizationsExport, {
        params: this._filterParams(filters),
      }),
    );
  }

  update(
    organizationId: string,
    payload: OrganizationEditPayload,
  ): Observable<OrganizationDetail | null> {
    const request = this._apiService.put<LaravelDefaultResponse<OrganizationShowResponse>>(
      endpoints.backoffice.organization(organizationId),
      { body: payload, skipError: true },
    );

    return this._apiService
      .run<LaravelDefaultResponse<OrganizationShowResponse>>(request)
      .pipe(map((response) => this.toDetail(response)));
  }

  updateBillingEmail(
    organizationId: string,
    billingEmail: string | null,
  ): Observable<OrganizationBillingEmailResponse> {
    const request = this._apiService.put<LaravelDefaultResponse<OrganizationBillingEmailResponse>>(
      endpoints.backoffice.organizationBillingEmail(organizationId),
      { body: { billing_email: billingEmail }, skipError: true },
    );

    return this._apiService
      .run<LaravelDefaultResponse<OrganizationBillingEmailResponse>>(request)
      .pipe(map((response) => response.data));
  }

  suspend(organizationId: string, reason: string): Observable<OrganizationDetail | null> {
    const request = this._apiService.post<LaravelDefaultResponse<OrganizationShowResponse>>(
      endpoints.backoffice.organizationSuspend(organizationId),
      { body: { reason }, skipError: true },
    );

    return this._apiService
      .run<LaravelDefaultResponse<OrganizationShowResponse>>(request)
      .pipe(map((response) => this.toDetail(response)));
  }

  reactivate(organizationId: string): Observable<OrganizationDetail | null> {
    const request = this._apiService.post<LaravelDefaultResponse<OrganizationShowResponse>>(
      endpoints.backoffice.organizationReactivate(organizationId),
      { body: {}, skipError: true },
    );

    return this._apiService
      .run<LaravelDefaultResponse<OrganizationShowResponse>>(request)
      .pipe(map((response) => this.toDetail(response)));
  }

  changeRoot(
    organizationId: string,
    userId: string,
    override: boolean,
  ): Observable<OrganizationRootChangeResponse> {
    const request = this._apiService.put<LaravelDefaultResponse<OrganizationRootChangeResponse>>(
      endpoints.backoffice.organizationRoot(organizationId),
      {
        body: { user_id: userId, override },
        skipError: true,
      },
    );

    return this._apiService
      .run<LaravelDefaultResponse<OrganizationRootChangeResponse>>(request)
      .pipe(map((response) => response.data));
  }

  private _params(
    filters: Partial<OrganizationFilters>,
    page: number,
    pageSize: number,
  ): Record<string, string | number> {
    return { ...this._filterParams(filters), page, pageSize };
  }

  private _filterParams(filters: Partial<OrganizationFilters>): Record<string, string> {
    const params: Record<string, string> = {};

    for (const [key, value] of Object.entries(filters)) {
      if (value !== null && value !== undefined && value !== '') {
        params[key] = String(value);
      }
    }

    return params;
  }
}
